import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import StoreIcon from '@mui/icons-material/Store';
import CategoryIcon from '@mui/icons-material/Category';
import api from '../services/api';

const AdminDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/dashboard/admin');
      setDashboard(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  };

  if (!dashboard) {
    return (
      <Container>
        <Typography>Carregando...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, pt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
        <Typography 
          variant="h3" 
          component="h1"
          sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 700,
          }}
        >
          Painel Administrativo
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PeopleIcon />}
            onClick={() => navigate('/admin/users')}
          >
            Gerenciar Usuários
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<StoreIcon />}
            onClick={() => navigate('/admin/stores')}
          >
            Gerenciar Lojas
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CategoryIcon />}
            onClick={() => navigate('/admin/categories')}
          >
            Categorias
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.05)' }
            }}
            onClick={() => navigate('/admin/users')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary">
                {dashboard.stats?.total_users || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Usuários
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button 
                size="small" 
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/admin/users');
                }}
              >
                Criar Usuário
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.05)' }
            }}
            onClick={() => navigate('/admin/stores')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <StoreIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary">
                {dashboard.stats?.total_stores || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Lojas
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button 
                size="small" 
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/admin/stores');
                }}
              >
                Criar Loja
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {dashboard.stats?.total_products || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Produtos
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {dashboard.stats?.total_favorites || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Favoritos
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, backgroundColor: 'action.hover' }}>
            <Typography variant="h6" gutterBottom>
              Ações Rápidas
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/users')}
              >
                Criar Novo Usuário
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/stores')}
              >
                Criar Nova Loja
              </Button>
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => navigate('/admin/users')}
              >
                Ver Todos os Usuários
              </Button>
              <Button
                variant="outlined"
                startIcon={<StoreIcon />}
                onClick={() => navigate('/admin/stores')}
              >
                Ver Todas as Lojas
              </Button>
              <Button
                variant="outlined"
                startIcon={<CategoryIcon />}
                onClick={() => navigate('/admin/categories')}
              >
                Gerenciar Categorias
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Produtos
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell>Loja</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboard.topProducts?.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>
                        <Chip label={product.category} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        {(Number(product.engagement_score) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>{product.store_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Lojas
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Loja</TableCell>
                    <TableCell align="right">Score Total</TableCell>
                    <TableCell align="right">Score Médio</TableCell>
                    <TableCell align="right">Produtos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboard.topStores?.map((store, index) => (
                    <TableRow key={store.id}>
                      <TableCell>
                        <Box
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => navigate(`/store/${store.id}`)}
                        >
                          {store.name}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {(Number(store.total_engagement) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {(Number(store.avg_engagement) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">{store.products_count || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminDashboard;
